export type TeamUserType = 'coach' | 'employee' | 'admin';
export type TeamMembershipRole = 'leader' | 'member';

export interface TeamMemberInput {
    userId: string;
    userType: TeamUserType;
}

export interface TeamRevenue {
    total: number;
    newSales: number;
    reSales: number;
    refund: number;
}

export interface Team {
    _id: string;
    centerId: string;
    name: string;
    description?: string;
    leaderUserId: string;
    leaderUserType: TeamUserType;
    isActive: boolean;
    deletedAt?: string;
    createdAt: string;
    updatedAt: string;
}

export interface TeamWithSummary extends Team {
    memberCount: number;
    monthRevenue: TeamRevenue;
}

export interface TeamMembership {
    _id: string;
    teamId: string;
    centerId: string;
    userId: string;
    userType: TeamUserType;
    role: TeamMembershipRole;
    joinedAt: string;
    leftAt?: string;
    isActive: boolean;
    /** getTeam 응답에서만 첨부 — 이번 달 매출 */
    monthRevenue?: TeamRevenue;
}

export interface CreateTeamBody {
    name: string;
    description?: string;
    leader: TeamMemberInput;
    members: TeamMemberInput[];
    effectiveFrom?: string; // ISO date
}

export interface BackfillResult {
    memberTicketPayment: { matched: number; updated: number; skipped: number };
    membershipPayment: { matched: number; updated: number; skipped: number };
}

export interface CreateTeamResponse {
    team: Team;
    members: TeamMembership[];
    backfillResult: BackfillResult;
}

export interface ListTeamsResponse {
    teams: TeamWithSummary[];
}

export interface GetTeamResponse {
    team: Team;
    members: TeamMembership[];
    monthRevenue: TeamRevenue;
}

export interface AddTeamMemberBody {
    userId: string;
    userType: TeamUserType;
    allowTransfer?: boolean;
}

// === 슈퍼관리자 분석 ===

export interface SuperAdminTeamRow {
    teamId: string;
    centerId: string;
    name: string;
    leaderUserId: string;
    leaderName?: string | null;
    memberCount: number;
    revenue: TeamRevenue;
    avgPerMember: number;
    isActive: boolean;
}

export interface SuperAdminTeamsResponse {
    totalCenterRevenue: number;
    totalTeamRevenue: number;
    unassignedRevenue: number;
    teams: SuperAdminTeamRow[];
}

export interface SuperAdminTeamBreakdownMember {
    userId: string;
    userName?: string | null;
    userType: TeamUserType;
    role: TeamMembershipRole;
    isActive: boolean;
    revenue: TeamRevenue;
}

export interface SuperAdminTeamBreakdownResponse {
    team: Team;
    members: SuperAdminTeamBreakdownMember[];
    daily: Array<{ date: string; revenue: number }>;
}

// === 강사 옵션 (선택 UI용) ===

export interface CenterCoachOption {
    coachId: string;
    employeeId: string;
    coachName: string;
    profileImageUrl: string;
    profileColorType?: string;
    profileName?: string;
    isConnected: boolean;
    isDeleted: boolean;
}

export interface CenterCoachListResponse {
    registeredTrainer: CenterCoachOption[];
    deletedTrainer: CenterCoachOption[];
}

// === 판매담당자 옵션 (강사 + 영업/판매담당) ===

export interface CenterSalesManager {
    role: 'coach' | 'etc' | string;
    userId: string;
    userName: string;
    isDeleted?: boolean;
}

export interface CenterSalesManagersResponse {
    salesManagers: CenterSalesManager[];
}

/**
 * salesManager.role → 팀 시스템의 userType 매핑.
 * - 'coach' → 'coach' (Coach 모델 검증)
 * - 'etc'   → 'admin' (Center.etcSalesManagers 검증 경로)
 */
export const salesManagerToUserType = (role: string): TeamUserType => {
    if (role === 'coach') return 'coach';
    return 'admin';
};
